import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { ProgressService } from '../../lib/progress.service';
import { Day, Lodge, Package, Product } from '@shared';
import { CurrencyPipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingComponent } from '../../loading/loading.component';

@Component({
  selector: 'app-booking-form-by-location',
  imports: [LoadingComponent, CurrencyPipe],
  templateUrl: './booking-form-by-location.component.html',
  styleUrl: './booking-form-by-location.component.css',
})
export class BookingFormByLocationComponent {
  public progressService = inject(ProgressService);
  public router = inject(Router);
  public route = inject(ActivatedRoute);
  public lodges = computed<Lodge[]>(() =>
    (this.progressService.selectedFestival()?.lodges || []).filter(
      (lodge) => lodge.public,
    ),
  );
  public daysOfSelectedLodge = computed<Day[]>(() => {
    if (!this.progressService.selectedLodge()) {
      return [];
    }

    let days = this.progressService.selectedLodge()?.days;

    if (this.progressService.selectedDayslot()) {
      days = this.progressService
        .selectedLodge()!
        .days?.filter(
          (day) =>
            day.daytype == this.progressService.selectedDayslot() &&
            day.date == this.progressService.selectedDate(),
        );
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    days = days?.filter(
      (day) => new Date(day.date || '').getTime() > today.getTime(),
    );

    return days ? days.filter((elem) => elem !== undefined) : [];
  });
  public selectedLodgePackages = computed<Package[]>(() => {
    if (!this.progressService.selectedLodge()) {
      return [];
    }

    const days = this.daysOfSelectedLodge();

    if (!days || days.length <= 0) {
      return [];
    }

    const packages = days
      .map((day) => day.packages)
      .flat()
      .filter((pkg) => pkg !== undefined);
    const uniquePackageIds = [
      ...new Set(
        packages
          .map((pkg) => pkg?.id || undefined)
          .filter((id) => id !== undefined),
      ),
    ];

    return (
      uniquePackageIds
        .map((id) => packages.find((pkg) => pkg.id === id))
        .filter((val) => val !== undefined)
        .flat() || []
    );
  });
  public selectedLodgeMinimumConsumptionCost = computed<number | null>(() => {
    if (!this.progressService.selectedLodge()) {
      return null;
    }

    const days = this.daysOfSelectedLodge();

    if (!days || days.length <= 0) {
      return null;
    }

    const minCostsPerDay = days
      .map((lodgeDaySlots) => {
        return lodgeDaySlots.products
          ? this.getCostForProducts(lodgeDaySlots.products)
          : undefined;
      })
      .filter((val) => val !== undefined)
      .filter((val) => val > 0);

    if (minCostsPerDay.length === 0) {
      return null;
    }

    return Math.min(...minCostsPerDay);
  });
  private lodgeConfirmButton = signal<ElementRef | null>(null);

  @ViewChild('lodgeButton', { static: false }) set lodgeButton(
    element: ElementRef,
  ) {
    if (element) {
      this.lodgeConfirmButton.set(element);
    }
  }

  getLodgeImageFilename(lodge: Lodge) {
    return `/lodge-${lodge.title
      .toLowerCase()
      .replace(/\s/gi, '-')
      .split('-')
      .filter((elem) => !elem.match(/.*\d.*/))
      .join('-')}.webp`;
  }

  getCostForProducts(products: Product[]) {
    return products
      ?.map(
        (product) =>
          +(product.pivot?.amount || 1) *
          (+product.price + +(product.bediengeld || 0)),
      )
      .reduce((prev, cur) => prev + cur, 0);
  }

  constructor() {
    effect(() => {
      const targetHtmlElement = this.lodgeConfirmButton();
      if (targetHtmlElement && this.progressService.selectedLodge()) {
        setTimeout(
          () =>
            targetHtmlElement.nativeElement.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
            }),
          20,
        );
        setTimeout(
          () =>
            targetHtmlElement.nativeElement.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
            }),
          150,
        );
      }
    });
  }

  isLodgeAvailable(lodge: Lodge) {
    if (!lodge.days || lodge.days.length == 0) {
      return false;
    }
    let days = lodge.days;

    if (this.progressService.selectedDayslot()) {
      days = lodge.days?.filter(
        (day) =>
          day.daytype == this.progressService.selectedDayslot() &&
          day.date == this.progressService.selectedDate(),
      );
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    days = days?.filter(
      (day) => new Date(day.date || '').getTime() > today.getTime(),
    );

    return !!days.find((day) => day.status == 'avail');
  }

  selectLodge(lodge: Lodge) {
    if (!lodge.selectable || !this.isLodgeAvailable(lodge)) {
      return;
    }
    this.progressService.lodgeSelected(lodge);
  }

  getMobileRowStart(lodge: Lodge) {
    return 23 - lodge.grid_conf.column.pos - lodge.grid_conf.column.span + 3;
  }

  getLodgeDisplayTitle(lodge: Lodge) {
    return (lodge.title_public || lodge.title)
      .replace('Mittel', 'Mit&shy;tel')
      .replace('zelt', '&shy;zelt')
      .replace('bereich', '&shy;be&shy;reich')
      .replace('Fest', 'Fest&shy;')
      .replace('schiff', '&shy;schiff')
      .replace('Stuttgart', 'Stutt&shy;gart&shy;')
      .replace('Cannstatter', 'Cann&shy;stat&shy;ter');
  }

  selectPackage(pkg: Package | undefined = undefined) {
    this.progressService.packageSelected(pkg || null);
    if (!this.progressService.selectedDayslot()) {
      this.router.navigate([
        'booking',
        this.route.snapshot.paramMap.get('eventSlug'),
        'date',
      ]);
    } else {
      this.router.navigate(['booking', 'persons']);
    }
  }
}
